import { useState } from "react";
import { Phone, Ghost, Music2, ArrowRight, Check, Copy } from "lucide-react";
import { CONTACT } from "@/data/site";
import { Reveal } from "@/lib/motion";
import { useEnquiry } from "@/context/EnquiryContext";

const CHANNELS = [
  { icon: Phone, label: "Call or text", value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-phone" },
  { icon: Ghost, label: "Snapchat", value: CONTACT.snapchat, href: CONTACT.snapchatHref, testid: "contact-snapchat" },
  { icon: Music2, label: "TikTok", value: CONTACT.tiktok, href: CONTACT.tiktokHref, testid: "contact-tiktok" },
];

export const Contact = () => {
  const [copied, setCopied] = useState(false);
  const { serviceText, setServiceText } = useEnquiry();

  const copyEnquiry = async () => {
    const val = (id) => (document.getElementById(id)?.value || "").trim();
    const msg = [
      "Fastlane Motorz enquiry",
      `Name: ${val("name")}`,
      `Phone: ${val("phone")}`,
      `Vehicle: ${val("vehicle")}`,
      "Services:",
      serviceText || "(none selected)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(msg);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = msg; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <section id="contact" data-testid="contact" className="relative z-10 bg-[#08090B] py-24 sm:py-28">
      <div className="container-x">
        <div className="relative overflow-hidden rounded-[28px] border border-[var(--line)] bg-gradient-to-b from-[#12161C] to-[#0B0E12]">
          <div className="absolute inset-x-0 top-0 z-10 h-px bg-gradient-to-r from-transparent via-[var(--blue-bright)]/60 to-transparent" />
          <div className="grid lg:grid-cols-[1.05fr_1fr]">
            <div className="relative p-9 sm:p-12">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(23,147,255,0.18),transparent_45%)]" />
              <div className="relative">
                <Reveal>
                  <span className="eyebrow mb-4"><span className="h-px w-8 bg-[var(--blue-bright)]" /> Get a Quote</span>
                  <h2 className="font-display text-4xl font-800 leading-tight tracking-tight text-white sm:text-5xl">
                    Ready to book your service?
                  </h2>
                  <p className="mt-4 max-w-md text-base text-[var(--muted)]">
                    Enquire about detailing, valeting, window tints, headlight tints, PPF, dashcam fitting,
                    driveway cleaning, gutter cleaning or fleet services. Cars, vans and fleets welcome —
                    mobile service available.
                  </p>
                </Reveal>

                <div className="mt-9 space-y-3">
                  {CHANNELS.map((c) => (
                    <a key={c.label} href={c.href} data-testid={c.testid}
                      target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                      className="group flex items-center gap-4 rounded-2xl border border-[var(--line)] bg-white/[0.02] px-5 py-4 transition-all duration-300 hover:border-[var(--blue)]/50 hover:bg-[var(--blue)]/[0.06]">
                      <span className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--blue)]/12 text-[var(--blue-bright)]">
                        <c.icon size={19} />
                      </span>
                      <span className="flex-1">
                        <span className="block text-xs uppercase tracking-[0.18em] text-[var(--muted)]">{c.label}</span>
                        <span className="block font-medium text-white">{c.value}</span>
                      </span>
                      <ArrowRight size={16} className="text-[var(--muted)] transition-transform duration-300 group-hover:translate-x-1 group-hover:text-[var(--blue-bright)]" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t border-[var(--line)] bg-[#0A0C10]/60 p-9 sm:p-12 lg:border-l lg:border-t-0">
              <form data-testid="contact-form" onSubmit={(e) => e.preventDefault()} className="space-y-4">
                <h3 className="font-display text-lg font-700 text-white">Build your enquiry</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Your name" id="name" placeholder="Jordan Smith" />
                  <Field label="Phone" id="phone" placeholder="07…" />
                </div>
                <Field label="Vehicle" id="vehicle" placeholder="e.g. Ford Focus, Transit van" />
                <div>
                  <label htmlFor="msg" className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-[var(--muted)]">Service needed</label>
                  <textarea id="msg" rows={4} value={serviceText} onChange={(e) => setServiceText(e.target.value)}
                    data-testid="contact-service-needed"
                    placeholder="Add services from the site above, or type here — e.g. Full valet, PPF front end, window tints…"
                    className="w-full resize-none rounded-xl border border-[var(--line)] bg-[#0E1116] px-4 py-3 text-sm text-white placeholder:text-[var(--muted)]/60 outline-none transition-colors focus:border-[var(--blue)]" />
                </div>

                <div data-testid="prewash-notice" className="rounded-xl border border-[var(--blue)]/30 bg-[var(--blue)]/10 px-4 py-3 text-xs leading-relaxed text-white/85">
                  <span className="font-semibold text-[var(--blue-bright)]">First-time customers receive a free pre-wash.</span> You must call or message the contact number to claim this offer.
                </div>

                <button type="button" data-testid="contact-copy" onClick={copyEnquiry} className="btn-electric w-full">
                  {copied ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy Enquiry</>}
                </button>
                <div className="grid gap-3 sm:grid-cols-2">
                  <a href={CONTACT.snapchatHref} target="_blank" rel="noreferrer" data-testid="contact-snap-btn" className="btn-ghost w-full !py-2.5">
                    <Ghost size={15} /> Message on Snapchat
                  </a>
                  <a href={CONTACT.phoneHref} data-testid="contact-call-btn" className="btn-ghost w-full !py-2.5">
                    <Phone size={15} /> Call or Message
                  </a>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, id, placeholder }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-[var(--muted)]">{label}</label>
    <input id={id} placeholder={placeholder}
      className="w-full rounded-xl border border-[var(--line)] bg-[#0E1116] px-4 py-3 text-sm text-white placeholder:text-[var(--muted)]/60 outline-none transition-colors focus:border-[var(--blue)]" />
  </div>
);
